import moment, { Duration } from 'moment'
import { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'

import { showBusy, showMessage } from './notification'

interface WrapOpts {
    stateFunc?(busy: boolean): void
    errorFunc?(error: any): void
    operation?: string
    showSuccess?: boolean
}

export async function invoke<T>(f :() => Promise<T>, opts?: WrapOpts) {
    try {
        if (opts?.stateFunc) {
            opts.stateFunc(true)
        } else {
            showBusy(true)
        }

        const r = await f()
        if (opts?.showSuccess) {
            showMessage(`Fuldført med succes ${opts.operation || 'Kontakt til Serveren'}.`, 'success')
        }

        return r
    } catch (error) {
        showMessage(`Der opstod en fejl under ${opts?.operation || 'Kontakt til Serveren'}!`, 'error')

        if (opts?.errorFunc) {
            opts.errorFunc(error)
        }

        throw error
    } finally {
        if (opts?.stateFunc) {
            opts.stateFunc(false)
        } else {
            showBusy(false)
        }
    }
}

export const useInvokeEffect = (f :() => Promise<any>, deps?: ReadonlyArray<any> | undefined, opts?: WrapOpts) => {
    useEffect(() => {
        invoke(f, opts).then()
    }, deps)
}

const zeroPad = (num: number, places: number = 2) => String(num).padStart(places, '0')

const formatDuration = (duration: Duration) => {
    if (duration.hours() > 0) {
        return `${zeroPad(duration.hours())} : ${zeroPad(duration.minutes())} : `
            + `${zeroPad(duration.seconds())}`
    } else if (duration.minutes() > 0) {
        return `00 : ${zeroPad(duration.minutes())} : ${zeroPad(duration.seconds())}`
    } else {
        return `00 : 00 : ${zeroPad(duration.seconds())} `
    }
}

export const formatSeconds = (seconds: number) => {
    return formatDuration(moment.duration(seconds, 'seconds'))
}

export const formatTimeline = (seconds: number) => {
    const minutes = Math.floor(seconds / 60)
    const hours = Math.floor(minutes / 60)

    if (hours > 0) {
        return `${hours}h  ${minutes - hours * 60}m`
    } else {
        return `${minutes}m`
    }

}

export const useHistoryBackOverride = (message: string, onBack?: () => void) => {
    const [$goBack, $setGoBack] = useState(false)
    const history = useHistory()

    const onBackButtonEvent = (e: any) => {
        e.preventDefault()
        if (!$goBack) {
            // eslint-disable-next-line no-alert
            if (window.confirm(message)) {
                if (onBack) {
                    onBack()
                }
                $setGoBack(true)
                history.push('/')
            } else {
                window.history.pushState(null, window.document.title, window.location.pathname)
                $setGoBack(false)
            }
        }
    }

    useEffect(() => {
        window.history.pushState(null, window.document.title, window.location.pathname)
        window.addEventListener('popstate', onBackButtonEvent)
        return () => {
            window.removeEventListener('popstate', onBackButtonEvent)
        }
    })
}

export const makeLocalUri = (pathname: string) => {
    const { protocol, port, hostname } = window.location
    return `${protocol}//${hostname}:${port || ''}${pathname}`
}

export function getFileInfo(file: any, mime: string = ''): { filename: string, mime: string } {
    const pos: number = String(file.name).lastIndexOf('.')

    if (mime === 'image/jpeg') {
        const filename = `${String(file.name)
            .substr(0, pos < 0 ? String(file.name).length : pos)}.jpg`

        return {
            filename,
            mime: 'image/jpeg'
        }
    }

    return {
        filename: file.name,
        mime: file.type,
    }
}
